import { Link } from "react-router-dom";
import { ArrowRight, Video, CalendarDays, BookOpen, Users } from "lucide-react";
import { PageHero, OutLink } from "@/components/site/PageBits";
import { SectionLabel } from "@/components/site/FormBits";
import { useSeo, pageSeo } from "@/lib/seo";

const SEO = pageSeo("/greg-miceli");

const SRC = "https://gregmiceli.com/digital-nomad";

const OFFERS = [
  { icon: CalendarDays, t: "Thursday workshops at Esc", d: "A 9-module masterclass on living and working anywhere — one live workshop every Thursday at Esc in Popoyo, Nicaragua. Dates to be announced." },
  { icon: Video, t: "Workshop replays", d: "A rolling library of episodes on remote-work life, culture, and community in San Juan del Sur." },
  { icon: BookOpen, t: "The Ultimate Digital Nomad Travel Guide to Nicaragua", d: "Greg's Nomad Pick · eBook Series #1 — 50 essential tips for traveling and working abroad." },
  { icon: Users, t: "Nicaragua community", d: "Meet other remote workers, entrepreneurs and creatives. Ask questions, share tips, and find your next co-working spot or meet-up." },
];

export default function GregMiceliPage() {
  useSeo(SEO);
  return (
    <article>
      <PageHero eyebrow="GREG MICELI · WORK • EXPLORE • BELONG" title="Greg Miceli" image="/brand/esc-clubhouse.jpg" imageAlt="Esc Digital Nomad Village clubhouse at dusk in Popoyo, Nicaragua"
        subtitle="Live, work, and thrive on the Pacific coast of Nicaragua. Watch the series, join a workshop, and plug into a community already doing it.">
        <OutLink href={SRC} testId="greg-external-link" light>Visit gregmiceli.com/digital-nomad</OutLink>
        <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-5 py-3 font-semibold hover:bg-[#D96F28]" data-testid="greg-contact-cta">Let's collaborate <ArrowRight className="h-4 w-4" /></Link>
      </PageHero>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <SectionLabel>Digital nomad workshops in Nicaragua</SectionLabel>
        <h2 className="mt-3 mb-10 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Ways to learn and connect</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {OFFERS.map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6 flex gap-4">
              <span className="h-11 w-11 shrink-0 rounded-xl bg-[#1A9AA8]/10 text-[#147C88] flex items-center justify-center"><Icon className="h-5 w-5" /></span>
              <div>
                <h3 className="font-semibold text-[#0B2A4A]">{t}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-8 grid lg:grid-cols-[1fr_1.3fr] gap-12 items-center">
        <img src="/brand/esc-logo.jpg" alt="Esc Digital Nomad Village logo" className="rounded-3xl w-full max-w-sm mx-auto" />
        <div>
          <SectionLabel>Meet the venue</SectionLabel>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Esc Digital Nomad Village, Popoyo</h2>
          <p className="mt-4 text-slate-700 leading-relaxed">You come for a workshop — and maybe stay a little longer than planned. Esc Digital Nomad Village sits on the Pacific coast in Popoyo: pool, fast wifi, coliving suites and the best sunsets in Central America.</p>
          <p className="mt-3 text-xs text-slate-500">Venue details as described on gregmiceli.com. Confirm current availability and arrangements directly before booking.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <div className="rounded-3xl bg-[#0B2A4A] text-[#F5ECD7] p-8 sm:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Commission-based collaboration, stated plainly</h2>
            <p className="mt-2 text-[#F5ECD7]/80 max-w-2xl">Greg is building a team for digital projects. This is not a job with a salary or guaranteed income — it's a commission-based opportunity for people who want to help build and grow digital projects.</p>
          </div>
          <Link to="/contact" className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-6 py-3.5 font-semibold" data-testid="greg-contact-cta-bottom">Tell me about you <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </article>
  );
}
